import { useEffect } from 'react';
import type { FC } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Drawer, Hidden } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import Scrollbar from '../Scrollbar';
import NavSection from '../NavSection';
import { Logo } from '../Logo';

interface DashboardSidebarProps {
    onMobileClose: () => void;
    openMobile: boolean;
}

const sections = [
    {
        title: 'Knowledge Base',
        items: [
            {
                title: 'Articles',
                path: '/kb/',
                icon: <ArticleIcon fontSize="small" />,
            },
            {
                title: 'Categories',
                path: '/kb/categories',
                icon: <CategoryIcon fontSize="small" />,
            },
        ],
    },
    {
        title: 'General',
        items: [
            {
                title: 'Settings',
                path: '/kb/settings',
                icon: <SettingsIcon fontSize="small" />,
            },
        ],
    },
];

export const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
    const { onMobileClose, openMobile } = props;
    const location = useLocation();

    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose();
        }
    }, [location.pathname]);

    const content = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <Scrollbar options={{ suppressScrollX: true }}>
                <Hidden lgUp>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: 2,
                        }}
                    >
                        <RouterLink to="/">
                            <Logo />
                        </RouterLink>
                    </Box>
                </Hidden>
                <Box sx={{ p: 2 }}>
                    {sections.map((section) => (
                        <NavSection
                            key={section.title}
                            pathname={location.pathname}
                            sx={{
                                '& + &': {
                                    mt: 3,
                                },
                            }}
                            {...section}
                        />
                    ))}
                </Box>
            </Scrollbar>
        </Box>
    );

    return (
        <>
            <Drawer
                anchor="left"
                onClose={onMobileClose}
                open={openMobile}
                PaperProps={{
                    sx: {
                        backgroundColor: 'background.paper',
                        width: 280,
                        display: { xs: 'block', lg: 'none' },
                    },
                }}
                variant="temporary"
            >
                {content}
            </Drawer>
            <Drawer
                anchor="left"
                open
                PaperProps={{
                    sx: {
                        backgroundColor: 'background.paper',
                        height: 'calc(100% - 64px) !important',
                        top: '64px !Important',
                        width: 280,
                        display: { xs: 'none', lg: 'block' },
                    },
                }}
                variant="persistent"
            >
                {content}
            </Drawer>
        </>
    );
};
